import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { f7 } from 'framework7-vue';
import { closeAdaptiveModal } from './useAdaptiveModal.js';
import _ from 'lodash';
import i18n from '@/js/i18n.js';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '../pinia-stores/account.js';

import { useLocationStore } from '../pinia-stores/location.js';
import { useApimoneyStore } from '../pinia-stores/apimoney.js';
import { useKeycloakStore } from '../pinia-stores/keycloak.js';

export function useAccount(options = {}) {
  const { f7route, f7router } = options;
  const instance = getCurrentInstance();
  const t = i18n.global.t;

  const accountStore = useAccountStore();

  const locationStore = useLocationStore();
  const apimoneyStore = useApimoneyStore();
  const keycloakStore = useKeycloakStore();

  const { userAccount, userGroupList } = storeToRefs(accountStore);

  const { country } = storeToRefs(locationStore);

  const KYC_Detail = ref([]);
  const amount = ref('');
  const trxType = ref('');
  const referenceId = ref('');
  const description = ref('');
  const tag = ref('');
  const maxNumberOfItem = ref(18);
  const setWalletCurrency = ref('');
  const userCtry = ref('');
  const baseUrl = ref('');
  const selectedAccount = ref(null);
  const actionsToPopover = ref(null);
  const isEditable = ref(false);
  const addNew = ref(false);
  const number = ref('');
  const bic = ref('');
  const account_id = ref('');
  const accountIsActive = ref(false);
  const accountStatus = ref('');
  const selectedCard = ref(null);
  const actionsCCToPopover = ref(null);
  const openWalletMenuToPopover = ref(null);
  const currentMainWallet = ref('');
  const contribToPopover = ref(null);
  const holder_name = ref('');
  const documentsUrl = ref([]);
  const entity = ref(null);
  const project = ref(null);
  const visibleItem = ref(null);
  const walletctry = ref(null);

  const mainWallet = computed(() => {
    if (userAccount.value && userAccount.value.userAccountWallets) {
      const wallets = [
        ...(userAccount.value.userAccountWallets.STANDARD || []),
        ...(userAccount.value.userAccountWallets.BUSINESS || []),
      ];
      return wallets.find((w) => w.main) || null;
    }
    return null;
  });

  const accountWallets = computed(() => {
    if (userAccount.value && userAccount.value.userAccountWallets) {
      const wallets = [];
      Object.keys(userAccount.value.userAccountWallets).forEach((type) => {
        wallets.push(...userAccount.value.userAccountWallets[type]);
      });
      return wallets;
    }
    return [];
  });

  const countries = computed(() => _.groupBy(country.value, 'continentName'));

  const walletCurrency = computed(() => {
    const ctry = country.value?.find((x) => x.countryCode2 === userCtry.value);
    return ctry ? ctry.currencyCode : '';
  });

  const computedWalletctry = computed(() => {
    return country.value?.find((x) => x.countryCode2 === userCtry.value) || null;
  });

  function updateUser() {
    setTimeout(() => {
      accountStore.loadAccount(keycloakStore.subject);
    }, 1000);
  }

  function getAccountStatus(status) {
    const statusMapping = {
      ACTIVE: t('common.active'),
      INACTIVE: t('common.inActive'),
      KYC_REQUIRED: t('common.kycRequired'),
      SUSPENDED: t('bankAccount.suspended'),
      default: t('bankAccount.inActive'),
    };
    return statusMapping[status] || statusMapping.default;
  }

  function getAccountType(type) {
    return type === 'STANDARD' ? t('common.standard') : t('common.business');
  }

  function getAccountKYC_Level() {
    const { kyc_level, type } = instance?.proxy?.activeAccount || {};
    const { apiCode } = userAccount.value || {};
    return KYC_Detail.value.find(
      (kYC) => kYC.name === kyc_level && kYC.account_type === type && kYC.apiCode.includes(apiCode)
    );
  }

  function getAccountKYC_Level_Up() {
    const activeAccount = instance?.proxy?.activeAccount;
    if (!activeAccount) return;
    const levelMapping = {
      LEVEL_0: 'LEVEL_1',
      LEVEL_1: 'LEVEL_2',
      LEVEL_2: 'LEVEL_3',
      default: 'LEVEL_0',
    };
    const upLevel = levelMapping[activeAccount.kyc_level] || levelMapping.default;
    return KYC_Detail.value.find(
      (kYC) =>
        kYC.name === upLevel &&
        kYC.account_type === activeAccount.type &&
        kYC.apiCode.includes(userAccount.value?.apiCode)
    );
  }

  function setStatus() {
    const statusToggle = f7.toggle.get('.toggle');
    accountStatus.value = statusToggle.checked ? 'ACTIVE' : 'INACTIVE';
    accountIsActive.value = statusToggle.checked ? true : false;
  }

  function addNewBankAccount() {
    addNew.value = !addNew.value;
  }

  function addBankAccount() {
    apimoneyStore.addBankAccount({
      account_id: account_id.value,
      tag: tag.value,
      number: number.value,
      bic: bic.value,
      holder_name: holder_name.value,
      file_content: documentsUrl.value[0]?.url,
    });
    updateUser();
  }

  function editBankAccount() {
    apimoneyStore.editBankAccount({
      bankAccount: { tag: tag.value, status: accountStatus.value },
      bankAccountId: selectedAccount.value.id,
    });
    updateUser();
  }

  function openItem(account) {
    selectedAccount.value = account;
    const buttonsList = [{ text: t('common.cancel'), color: 'red' }];
    if (!account.main) {
      buttonsList.unshift({
        text: t('common.delete'),
        color: 'deeporange',
        bold: true,
        onClick() {
          apimoneyStore.deleteBankAccount(account.id);
          updateUser();
        },
      });
    }
    buttonsList.unshift({
      text: t('common.edit'),
      color: 'yellow',
      bold: true,
      onClick() {
        isEditable.value = true;
        fillEditForm(account);
      },
    });
    if (!account.main) {
      buttonsList.unshift({
        text: t('common.setAsMainBankAccount'),
        color: 'primary',
        bold: true,
        onClick() {
          accountStore.setAccounMaintBankAccount({
            bankAccountId: account.id,
            accountId: account.account_id,
          });
        },
      });
    }
    actionsToPopover.value = f7.actions.create({
      buttons: buttonsList,
      targetEl: instance?.proxy?.$el?.querySelector(`#account${account.id}`),
    });
    actionsToPopover.value.open();
  }

  function fillEditForm(account) {
    number.value = account.number;
    bic.value = account.bic;
    tag.value = account.tag;
    account_id.value = account.account_id;
    accountIsActive.value = account.status === 'ACTIVE';
    accountStatus.value = account.status;
  }

  function deleteBankAccount(account) {
    f7.dialog.confirm(
      t('common.areYouSureToDeleteCreditCard', {
        currentAccount: `${account.number} ${account.tag}`,
      }),
      () => {
        apimoneyStore.deleteCreditCard(account.id);
      }
    );
    updateUser();
  }

  function openCreditCardItem(creditcard) {
    selectedCard.value = creditcard;
    actionsCCToPopover.value = f7.actions.create({
      buttons: [
        {
          text: selectedCard.value.main ? '' : t('common.setAsMainCreditCard'),
          color: selectedCard.value.main ? 'white' : 'primary',
          bold: selectedCard.value.main ? false : true,
          onClick() {
            if (selectedCard.value.main) return;
            accountStore.setAccounMaintCreditCard({
              creditcardId: selectedCard.value.id,
              accountId: selectedCard.value.account_id,
            });
          },
        },
        {
          text: t('common.edit'),
          bold: true,
          color: 'yellow',
          onClick() {
            editCreditCard(selectedCard.value);
          },
        },
        {
          text: selectedCard.value.main ? '' : t('common.delete'),
          color: selectedCard.value.main ? 'white' : 'deeporange',
          bold: selectedCard.value.main ? false : true,
          onClick() {
            if (selectedCard.value.main) return;
            deleteCreditCard(selectedCard.value);
          },
        },
        { text: t('common.cancel'), color: 'red' },
      ],
      targetEl: instance?.proxy?.$el?.querySelector(`#creditcard_${selectedCard.value.id}`),
    });
    actionsCCToPopover.value.open();
  }

  function editCreditCard(creditcard) {
    f7.dialog.prompt(
      t('common.updateCreditCard'),
      (tagVal) => {
        apimoneyStore.editCreditCard({
          creditCard: { tag: tagVal },
          creditCardId: creditcard.id,
        });
      },
      () => { return; },
      creditcard.tag
    );
    updateUser();
  }

  function deleteCreditCard(creditcard) {
    f7.dialog.confirm(
      t('common.areYouSureToDeleteCreditCard', {
        currentCard: `${creditcard.number} ${creditcard.tag}`,
      }),
      () => {
        apimoneyStore.deleteCreditCard(creditcard.id);
      }
    );
    updateUser();
  }

  function addApiWallet() {
    apimoneyStore.createApiWallet({
      account_id: instance?.proxy?.activeAccount?.id,
      tag: tag.value,
      status: 'ACTIVE',
      type: 'EMONEY',
      walletCategory: 'MISC',
      shared: false,
      currency: computedWalletctry.value?.currencyCode,
      countryCode: computedWalletctry.value?.countryCode2,
    });
    updateUser();
    setTimeout(() => {
      closeAdaptiveModal('.addNewApiWallet');
    }, 500);
  }



  function editApiWallet(walletItem) {
    f7.dialog.prompt(
      t('common.updateWallet'),
      (tagVal) => {
        apimoneyStore.editApiWallet({ apiWallet: { id: walletItem.id, tag: tagVal }, walletId: walletItem.id });
      },
      () => { return; },
      walletItem.tag
    );
    updateUser();
  }

  function deleteApiWallet(walletItem) {
    f7.dialog.confirm(
      t('common.areYouSureToDeleteWallet', { currentWallet: `${walletItem.id} ${walletItem.tag}` }),
      () => { apimoneyStore.deleteApiWallet(walletItem.id); }
    );
    updateUser();
  }

  function setAccountMainWallet() {
    if (currentMainWallet.value !== '' && currentMainWallet.value !== mainWallet.value?.id) {
      const cur_wallet = accountWallets.value.find((w) => w.id === currentMainWallet.value);
      f7.dialog.confirm(
        t('common.doYouWantToAssignOrChangeMainWallet', {
          walletTag: cur_wallet?.tag,
          acccountTag: instance?.proxy?.activeAccount?.tag,
        }),
        () => {
          accountStore.setAccounMaintWallet({ walletId: currentMainWallet.value, accountId: instance?.proxy?.activeAccount?.id });
        }
      );
      updateUser();
    }
  }

  // function transferFrom(walletItem) {
  //   f7.preloader.show('multi');
  //   const idempotency_key = f7.utils.id('xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
  //   const baseTransfer = {
  //     amount: '',
  //     trxType: walletItem.walletCategory,
  //     referenceId: '',
  //     receiver_wallet_id: '',
  //     groupId: walletItem.shared ? entity.value?.id : '',
  //     transaction_type: 'CASH_OUT',
  //     sender_wallet_id: walletItem.id,
  //     senderWallet: walletItem,
  //     payer_account_id: walletItem.account_id,
  //     description: description.value === '' ? walletItem.tag : description.value,
  //     countryCode: walletItem.countryCode,
  //     currency: walletItem.currency,
  //     tag: tag.value === '' ? walletItem.tag : tag.value,
  //     partner_ref: idempotency_key,
  //     idempotency_key,
  //     return_url: `${baseUrl.value}/transaction/success`,
  //     return_url_fail: `${baseUrl.value}/transaction/failure`,
  //   };
  //   accountStore.loadUser({ walletId: walletItem.id, type: 'walletId' });
  //   apimoneyStore.setBaseTransfer(baseTransfer);
  //   const payment_detail = {
  //     paymentCurrency: walletItem.currency,
  //     entity: entity.value,
  //     project: project.value,
  //     wallet: walletItem,
  //     transaction_type: 'CASH_OUT',
  //     tag: tag.value === '' ? walletItem.tag : tag.value,
  //     setAmount: wallet.value?.balance,
  //   };
  //   const urlParam = f7.utils.id();
  //   const dataToSave = { time_stamp: new Date().getTime() + 1000 * 60 * 5, baseTransfer, payment_detail };
  //   window.__piniaLcStorage.set(urlParam, dataToSave);
  //   f7router.navigate(`/pay/${urlParam}`, { props: { payment_detail } });
  //   f7.preloader.hide();
  // }

  // function topUp(walletItem) {
  //   f7.preloader.show('multi');
  //   const idempotency_key = f7.utils.id('xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
  //   const baseTransfer = {
  //     amount: amount.value,
  //     trxType: trxType.value,
  //     referenceId: referenceId.value,
  //     groupId: walletItem.shared && entity.value ? entity.value.id : '',
  //     receiver_wallet_id: walletItem.id,
  //     receiverWallet: walletItem,
  //     transaction_type: 'CASH_IN',
  //     sender_wallet_id: keycloakStore.authenticated
  //       ? userAccount.value?.standardWallet?.id
  //       : '',
  //     payer_account_id: walletItem.account_id,
  //     description: description.value === '' ? walletItem.tag : description.value,
  //     tag: tag.value === '' ? walletItem.tag : tag.value,
  //     countryCode: walletItem.countryCode,
  //     currency: walletItem.currency,
  //     partner_ref: idempotency_key,
  //     idempotency_key,
  //     return_url: `${baseUrl.value}/transaction/success`,
  //     return_url_fail: `${baseUrl.value}/transaction/failure`,
  //   };
  //   accountStore.loadUser({ walletId: walletItem.id, type: 'walletId' });
  //   const payment_detail = {
  //     paymentCurrency: walletItem.currency,
  //     entity: entity.value,
  //     project: project.value,
  //     wallet: walletItem,
  //     transaction_type: 'CASH_IN',
  //     tag: tag.value === '' ? walletItem.tag : tag.value,
  //     setAmount: amount.value,
  //   };
  //   const urlParam = f7.utils.id();
  //   const dataToSave = { time_stamp: new Date().getTime() + 1000 * 60 * 5, baseTransfer, payment_detail };
  //   window.__piniaLcStorage.set(urlParam, dataToSave);
  //   f7router.navigate(`/pay/${urlParam}`, { props: { payment_detail } });
  //   f7.preloader.hide();
  // }

  // async function selectProjectToContribute(walletItem) {
  //   let projectList = [];
  //   let entityName;
  //   if (walletItem.shared) {
  //     const proj = getSharedApiWalletRefType(walletItem);
  //     if (proj) {
  //       projectList = proj.projectList;
  //       trxType.value = proj.trxType;
  //       entity.value = proj.entity;
  //       entityName = entity.value.name;
  //       const buttonsList = [{ text: t('common.cancel'), color: 'red' }];
  //       for (const campaign of projectList.slice()) {
  //         let name;
  //         let numberVal;
  //         if (trxType.value === 'SAVB') {
  //           name = campaign.name; numberVal = campaign.battleNumber;
  //           tag.value = `${name} - ${entity.value.name}`; description.value = name; amount.value = campaign.contribAmount;
  //         } else if (trxType.value === 'TONT') {
  //           name = campaign.name; numberVal = campaign.roundNumber;
  //           tag.value = `${name} - ${entity.value.name}`; description.value = name; amount.value = campaign.tontineRoundAmount;
  //         } else if (trxType.value === 'GBUY') {
  //           name = campaign.name; numberVal = campaign.projectNumber;
  //           tag.value = `${name} - ${entity.value.name}`; description.value = name; amount.value = campaign.contribAmount;
  //         } else {
  //           name = campaign.name; numberVal = campaign.campaignNumber;
  //           tag.value = `${name} - ${entity.value.name}`; description.value = name;
  //           amount.value = trxType.value === 'ASSO' ? campaign.campaignAmount : campaign.fundingTarget;
  //         }
  //         buttonsList.unshift({
  //           text: `${name}  <span class="badge color-auto">${numberVal}</span>`,
  //           onClick() {
  //             project.value = campaign;
  //             referenceId.value = campaign.id;
  //             setTimeout(() => { topUp(walletItem); }, 300);
  //           },
  //         });
  //       }
  //       if (projectList && projectList.length == 1) {
  //         referenceId.value = projectList[0].id;
  //         topUp(walletItem);
  //       } else {
  //         buttonsList.unshift(
  //           { text: entityName, label: true },
  //           { text: t('common.select'), color: 'auto', bold: true }
  //         );
  //         contribToPopover.value = f7.actions.create({
  //           convertToPopover: true,
  //           closeByOutsideClick: true,
  //           closeByBackdropClick: true,
  //           closeOnEscape: true,
  //           buttons: buttonsList,
  //           targetEl: instance?.proxy?.$el?.querySelector(`#wallet_${walletItem.id}`),
  //         });
  //         await contribToPopover.value.open();
  //       }
  //     } else {
  //       tag.value = walletItem.tag;
  //       trxType.value = walletItem.walletCategory;
  //       topUp(walletItem);
  //     }
  //   } else {
  //     topUp(walletItem);
  //   }
  // }

  // function getSharedApiWalletRefType(walletItem) {
  //   const walletId = walletItem.id;
  //   if (!walletItem.shared) { topUp(walletItem); }
  //   getUserGroups(walletItem.walletCategory);
  //   setTimeout(() => {
  //     const walletLists = [
  //       { list: publicWalletList.value, type: walletItem.walletCategory, key: 'campaignList' },
  //       { list: privateWalletList.value, type: walletItem.walletCategory, key: 'campaignList' },
  //       { list: associationList.value, type: 'ASSO', key: 'associationCampaignList' },
  //       { list: tontineList.value, type: walletItem.walletCategory, key: 'tontineRoundList' },
  //       { list: groupBuyList.value, type: walletItem.walletCategory, key: 'projectList' },
  //       { list: shopList.value, type: 'SHOP', key: 'privateShopList' },
  //     ];
  //     for (const walletList of walletLists) {
  //       const found = _.find(walletList.list, { walletId });
  //       if (found) {
  //         return { entity: found, projectList: found[walletList.key], trxType: walletList.type };
  //       }
  //     }
  //     for (const sav of savings.value || []) {
  //       for (const batle of sav.battleList) {
  //         return {
  //           projectList: _.find(battle?.participantList, { walletId }),
  //           trxType: walletItem.walletCategory,
  //           entity: savings.value,
  //         };
  //       }
  //     }
  //   }, 500);
  // }

  // async function getUserGroups(category) {
  //   switch (category) {
  //     case 'ASSO':
  //       if (!associationList.value) {
  //         await assoStore.getUserAssociationsByuserId({ maxNumberOfItem: maxNumberOfItem.value });
  //       }
  //       break;
  //     case 'WALL':
  //       if (!privateWalletList.value) {
  //         await walletStore.getPrivateWalletList({ maxNumberOfItem: maxNumberOfItem.value, userId: keycloakStore.subject });
  //       }
  //       if (!publicWalletList.value) {
  //         walletStore.getPublicWalletList(visibleItem.value);
  //       }
  //       break;
  //     case 'TONT':
  //       if (!tontineList.value) {
  //         await tontineStore.getUserTontinesByuserId({ maxNumberOfItem: maxNumberOfItem.value, userId: keycloakStore.subject });
  //       }
  //       break;
  //     case 'GBUY':
  //       if (!groupBuyList.value) {
  //         await groupBuyStore.getUserGroupBuyList({ maxNumberOfItem: maxNumberOfItem.value, userId: keycloakStore.subject });
  //       }
  //       break;
  //     case 'SAVB':
  //       if (!savings.value) {
  //         await savingsStore.getUserSavingsGroupList({ maxNumberOfItem: maxNumberOfItem.value, userId: keycloakStore.subject });
  //       }
  //       break;
  //     case 'SHOP':
  //       if (!shopList.value) {
  //         await shopStore.getUserShopList({ maxNumberOfItem: maxNumberOfItem.value });
  //       }
  //       break;
  //   }
  // }

  onMounted(() => {
    if (userAccount.value === null && keycloakStore.authenticated) {
      accountStore.loadAccount(keycloakStore.subject);
    }
    if (keycloakStore.authenticated && userGroupList.value.length === 0) {
      accountStore.getMyGroupList(keycloakStore.subject);
    }
    baseUrl.value = window.location.origin.includes('http://localhost:')
      ? window.location.origin
      : import.meta.env.VITE_AVOCASH_SITE_URL;
    userCtry.value = userAccount.value?.countryCode || instance?.proxy?.profile?.countryCode;
    KYC_Detail.value = [
      {
        account_type: 'STANDARD', name: 'LEVEL_0',
        translated: t('common.level0', { level0: 0 }),
        mandatory_data: [t('common.emailOrPhoneNumber'), t('common.lastName'), t('common.firstName')],
        document: [], wallet_creation: 'Not possible', max_balance: 0, max_cash_in: 'n/a', max_cash_out: 'n/a',
        currency: 'EUR', value: 0, apiCode: ['api_money'], other_details: t('common.level0Details'),
      },
      {
        account_type: 'STANDARD', name: 'LEVEL_1',
        translated: t('common.level0', { level0: 1 }),
        mandatory_data: [t('common.level0Data', { level: 0 }), t('common.dateOfBirth'), t('common.cityOfBirth'), t('common.countryOfBirth')],
        document: [], optional_data: [], wallet_creation: 'Possible', max_balance: 150, max_cash_in: 150, max_cash_out: 150,
        currency: 'EUR', value: 1, apiCode: ['api_money'], other_details: t('common.level1OtherDetails'),
      },
      {
        account_type: 'STANDARD', name: 'LEVEL_2',
        translated: t('common.level0', { level0: 2 }),
        mandatory_data: [
          t('common.level0Data', { level: 1 }), t('common.nationality'), t('common.residenceCountry'),
          t('common.incomes'), t('common.professionalCategory'), t('common.aValidBankAccount'),
        ],
        document: [t('common.proofOfId'), t('common.proofOfInban')], optional_data: [], wallet_creation: 'Possible',
        max_balance: 10_000, max_cash_in: 100_000, max_cash_out: 'unlimited', currency: 'EUR', value: 2, apiCode: ['api_money'],
        other_details: t('common.level2OtherDetails'),
      },
      {
        account_type: 'STANDARD', name: 'LEVEL_3',
        translated: t('common.level0', { level0: 3 }),
        mandatory_data: [
          t('common.level0Data', { level: 2 }),
          `${t('common.fiscaldata')} :  ${t('common.whetherOrNot')} ${t('common.citizenOfUS')}; ${t('common.whetherOrNot')} ${t('common.fiscalResidentOfUS')}; ${t('common.whetherOrNot')} ${t('common.fiscalResidentOutOfFrance')}`,
        ],
        document: [t('common.secondProofOfIdOrTaxNotice')], optional_data: [], wallet_creation: 'Possible',
        max_balance: 100_000, max_cash_in: 1_000_000, max_cash_out: 'Unlimited', currency: 'EUR', value: 2, apiCode: ['api_money'],
        other_details: "Une seconde pièce d'identité : de même que la 1er pièce d'identité, celle-ci doit être conforme aux informations renseignées sur API-Money lors de la création du compte et doit être valide.",
      },
      {
        account_type: 'BUSINESS', name: 'LEVEL_1',
        translated: t('common.level0', { level0: 1 }),
        mandatory_data: [
          t('common.entityName'),
          `${t('common.businessType')}: ${t('common.company')}, ${t('common.association')}, ${t('common.soleTrader')}`,
          t('common.registrationNumber'), t('common.represenativeFirstName'), t('common.represenativeLastname'),
          t('common.represenativeBirthdate'), t('common.entityEmail'),
        ],
        optional_data: [t('common.represenativeNationality'), t('common.entityAddress')],
        document: [], wallet_creation: 'Not possible(1)', max_balance: 0, max_cash_in: 'n/a', max_cash_out: 'n/a',
        currency: 'EUR', value: 1, apiCode: ['api_money'],
        other_details: "(1) Depending of your use case, having a Wallet at KYC LEVEL_1 may be possible (crowdfunding, crowdlening, money pot...). It permits to start receiving cash-in but account is automatically set at 'KYC_REQUIRED' status after the first cash-in.",
      },
      {
        account_type: 'BUSINESS', name: 'LEVEL_2',
        translated: t('common.level0', { level0: 2 }),
        mandatory_data: [t('common.represenativeNationality'), t('common.entityAddress'), t('common.aValidBankAccount')],
        optional_data: [],
        document: [t('common.proofOfRegistration'), t('common.representativeProofOfId'), t('common.proofOfAddress'), t('common.proofOfInban'), t('common.businessStatus'), t('common.accountingReport')],
        wallet_creation: 'Possible', max_balance: 10_000_000, max_cash_in: 100_000_000, max_cash_out: 'Unlimited',
        currency: 'EUR', value: 2, apiCode: ['api_money'],
        other_details: "(1) Depending of your use case, having a Wallet at KYC LEVEL_1 may be possible (crowdfunding, crowdlening, money pot...).",
      },
    ];
  });

  return {
    KYC_Detail, amount, trxType, referenceId, description, tag, maxNumberOfItem,
    setWalletCurrency, userCtry, baseUrl, selectedAccount, actionsToPopover, isEditable,
    addNew, number, bic, account_id, accountIsActive, accountStatus, selectedCard,
    actionsCCToPopover, openWalletMenuToPopover, currentMainWallet, contribToPopover,
    holder_name, documentsUrl,  visibleItem, walletctry: computedWalletctry,
    userAccount, userGroupList, savings, groupBuyList,
    country, mainWallet, accountWallets, countries, walletCurrency,
    updateUser, getAccountStatus, getAccountType, getAccountKYC_Level, getAccountKYC_Level_Up,
    setStatus, addNewBankAccount, addBankAccount, editBankAccount, openItem, fillEditForm,
    deleteBankAccount, openCreditCardItem, editCreditCard, deleteCreditCard, addApiWallet,
    openWalletMenu, editApiWallet, deleteApiWallet, setAccountMainWallet,getSharedApiWalletRefType, getUserGroups
  };

}
